// components/CommonTable.js
import { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setState } from "./redux/schoolQuizSlice";

const CommonTable = ({
  total,
  columns,
  data,
  fetchData,
  filteredCount,
  initialRowsPerPage = 50,
  searchableKeys = [],
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState("None");
  const { newQuestionForm } = useSelector((state) => state.lawCollegeQuiz);
  const dispatch = useDispatch();
  // Fetch first page whenever rowsPerPage changes
  useEffect(() => {
    setCurrentPage(1);
    fetchData({ limit: rowsPerPage, offset: 0 });
  }, [rowsPerPage]);

  // Filtering (client-side)
  const filteredData = useMemo(() => {
    let result = data;

    // Filter by difficulty level if selected
    // if (newQuestionForm.difficulty_level) {
    //   result = result.filter(
    //     (q) => q.difficulty_level === newQuestionForm.difficulty_level
    //   );
    // }

    // Filter by search term if provided
    if (searchTerm) {
      result = result.filter((row) =>
        searchableKeys.some((key) =>
          String(row[key] || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        )
      );
    }

    return result;
  }, [data, searchTerm, searchableKeys, newQuestionForm.difficulty_level]);
  // Sorting (client-side)
  const sortedData = useMemo(() => {
    if (sortKey === "None") return filteredData;
    return [...filteredData].sort((a, b) => {
      if (a[sortKey] < b[sortKey]) return -1;
      if (a[sortKey] > b[sortKey]) return 1;
      return 0;
    });
  }, [filteredData, sortKey]);

  // For server-side pagination: don't slice data again
  const paginatedData = sortedData;

  // Handle page change
  const handlePageChange = (page) => {
    const newPage = Math.max(1, page);
    setCurrentPage(newPage);
    dispatch(
      setState({
        key: "offset",
        value: (newPage - 1) * rowsPerPage,
      })
    );
    fetchData({
      limit: rowsPerPage,
      offset: (newPage - 1) * rowsPerPage,
    });
  };

  return (
    <div style={{ marginTop: "20px" }}>
      <div style={{ display: "flex", gap: "2%" }}>
        <p>
          Total Count : <strong>{total}</strong>
        </p>
        <p>
          Filtered Count : <strong>{filteredCount}</strong>
        </p>
        <p>
          Matched Records Count : <strong>{sortedData.length}</strong>
        </p>
      </div>

      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "20px",
          marginBottom: "15px",
        }}
      >
        {/* {newQuestionForm.difficulty_level} */}

        {/* Pagination */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <strong>Pages: </strong>
          <button
            style={{ marginRight: "4%" }}
            onClick={() => handlePageChange(currentPage - 1)}
          >{`<`}</button>
          <input
            type="number"
            value={currentPage}
            min={1}
            style={{ width: "70px" }}
            onChange={(e) => handlePageChange(Number(e.target.value))}
          />
          <button
            onClick={() => handlePageChange(currentPage + 1)}
          >{`>`}</button>
        </div>

        {/* Rows per page */}
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <strong>Rows per page:</strong>
          <select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
          >
            {[10, 25, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        {/* Search */}
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <strong>Search:</strong>
          <input
            type="text"
            placeholder="Search ..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // Reset to first page on search
            }}
            style={{
              borderRadius: "5px",
              border: "1px solid gray",
              padding: "3px 6px",
            }}
          />
        </div>

        {/* Sort */}
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <strong>Sort by:</strong>
          <select value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
            <option>None</option>
            {columns
              .filter(
                (col) =>
                  ![
                    "Action (s)",
                    "ID",
                    "Created By",
                    "Time Stamp",
                    "Error Message",
                  ].includes(col.label)
              )
              .map(
                (col) =>
                  col.key && (
                    <option key={col.key} value={col.key}>
                      {col.label}
                    </option>
                  )
              )}
          </select>
        </div>
      </div>

      {/* Table */}
      <table className="error-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key || col.label}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {paginatedData.length > 0 ? (
            paginatedData.map((row, idx) => (
              <tr key={row.id || idx}>
                {columns.map((col) => (
                  <td key={col.key || idx}>
                    {col.render ? col.render(row) : row[col.key] || "NULL"}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length} style={{ textAlign: "center" }}>
                No rows found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default CommonTable;
